import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { accountService } from '../../../utils/supabaseQueries';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import LoadingIndicator from '../../../components/ui/LoadingIndicator';
import Icon from '../../../components/AppIcon';

const ProfileTab = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    company: '',
    bio: '',
    avatar_url: ''
  });
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Load user profile on component mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        setError(null);

        const { data, error } = await accountService?.getCurrentUserProfile();
        if (error) throw error;

        setProfile({
          full_name: data?.full_name || '',
          email: data?.email || user?.email || '',
          company: data?.company || '',
          bio: data?.bio || '',
          avatar_url: data?.avatar_url || ''
        });
      } catch (error) {
        console.error('Error loading profile:', error);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear success message when user starts editing
    if (success) setSuccess(null);
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const updates = {
        full_name: profile?.full_name,
        company: profile?.company,
        bio: profile?.bio
      };

      const { error } = await accountService?.updateUserProfile(updates);
      if (error) throw error;

      setSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailUpdate = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const { error } = await accountService?.updateUserEmail(profile?.email);
      if (error) throw error;

      setSuccess('Email update initiated. Please check your new email for confirmation.');
      setIsEditingEmail(false);
    } catch (error) {
      console.error('Error updating email:', error);
      setError('Failed to update email. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file?.size > 5 * 1024 * 1024) {
        setError('Avatar file size must be less than 5MB.');
        return;
      }

      // Validate file type
      if (!file?.type?.startsWith('image/')) {
        setError('Avatar must be an image file.');
        return;
      }

      setAvatarFile(file);
      setError(null);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    try {
      setIsUploadingAvatar(true);
      setError(null);

      const { data, error } = await accountService?.uploadAvatar(avatarFile);
      if (error) throw error;

      setProfile(prev => ({
        ...prev,
        avatar_url: data?.avatar_url
      }));

      setAvatarFile(null);
      setSuccess('Avatar updated successfully!');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setError('Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  if (isLoading) {
    return <LoadingIndicator message="Loading profile..." />;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="User" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Profile Information</h2>
          <p className="text-sm text-muted-foreground">Update your personal details and profile picture</p>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-green-600" />
            <p className="text-sm text-green-600">{success}</p>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {/* Avatar Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Profile Picture
          </label>
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border">
              {profile?.avatar_url ? (
                <img
                  src={profile?.avatar_url}
                  alt="Profile avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon name="User" size={24} className="text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
                id="avatar-upload"
              />
              <label
                htmlFor="avatar-upload"
                className="inline-flex items-center px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors"
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Choose File
              </label>
              {avatarFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">{avatarFile?.name}</span>
                  <Button
                    size="sm"
                    onClick={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                  >
                    {isUploadingAvatar ? (
                      <>
                        <Icon name="Loader" size={14} className="mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      'Upload'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Full Name *
          </label>
          <Input
            value={profile?.full_name}
            onChange={(e) => handleInputChange('full_name', e?.target?.value)}
            placeholder="Enter your full name"
            disabled={isSaving}
          />
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Email Address *
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <Input
                type="email"
                value={profile?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                placeholder="Enter your email address"
                disabled={!isEditingEmail || isSaving}
              />
            </div>
            {!isEditingEmail ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditingEmail(true)}
              >
                <Icon name="Edit2" size={14} className="mr-2" />
                Change
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={handleEmailUpdate}
                  disabled={isSaving}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditingEmail(false);
                    setProfile(prev => ({
                      ...prev,
                      email: user?.email || ''
                    }));
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {isEditingEmail && (
            <p className="text-xs text-muted-foreground mt-1">
              You'll receive a confirmation email at your new address
            </p>
          )}
        </div>

        {/* Company Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Company
          </label>
          <Input
            value={profile?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
            placeholder="Enter your company name"
            disabled={isSaving}
          />
        </div>

        {/* Bio Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Bio
          </label>
          <textarea
            value={profile?.bio}
            onChange={(e) => handleInputChange('bio', e?.target?.value)}
            placeholder="Tell us about yourself..."
            disabled={isSaving}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Brief description for your profile. Maximum 500 characters.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => {
              setProfile({
                full_name: '',
                email: user?.email || '',
                company: '',
                bio: '',
                avatar_url: ''
              });
              setError(null);
              setSuccess(null);
            }}
            disabled={isSaving}
          >
            Reset
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={isSaving || !profile?.full_name}
          >
            {isSaving ? (
              <>
                <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;